import { animate, style, transition, trigger } from '@angular/animations';
import { AsyncPipe, DatePipe } from '@angular/common';
import {
  Component,
  computed,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  doc,
  docData,
  DocumentData,
  Firestore,
  increment,
  serverTimestamp,
  setDoc,
  updateDoc,
} from '@angular/fire/firestore';
import { FormBuilder, FormGroup, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import {
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AiEvaluationResult } from '../../../../models/ai.model';
import { Group } from '../../../../models/group.model';
import {
  QuestionType,
  QuizQuestion,
} from '../../../../models/question.quiz.model';
import { Quiz, QuizStatus } from '../../../../models/quiz.model';
import { UserModel } from '../../../../models/user.model';
import { QuestionTypePipe } from '../../../../pipes/questionType.pipe';
import { AiService } from '../../../../services/ai.service';
import { QuizService } from '../../../../services/quiz.service';
import { UtilsService } from '../../../../services/utils.service';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { BtnRouteComponent } from '../../../components/buttons/btn-route/btn-route.component';

@Component({
  selector: 'app-active-quiz',
  standalone: true,
  imports: [
    FormsModule,
    AsyncPipe,
    BtnRouteComponent,
    BtnActionComponent,
    DatePipe,
    QuestionTypePipe,
  ],
  templateUrl: './active-quiz.component.html',
  animations: [
    // Animation for the Backdrop (Fade)
    trigger('fade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms ease-in', style({ opacity: 0 }))]),
    ]),
    // Animation for the Panel (Slide)
    trigger('slideInOut', [
      transition(
        ':enter',
        [
          // Start state: if mobile, below screen; if desktop, off-right
          style({ transform: '{{startPos}}' }),
          animate(
            '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
            style({ transform: 'translate(0,0)' }),
          ),
        ],
        { params: { startPos: 'translateX(100%)' } },
      ), // Default param
      transition(
        ':leave',
        [animate('250ms ease-in', style({ transform: '{{startPos}}' }))],
        { params: { startPos: 'translateX(100%)' } },
      ),
    ]),
  ],
  styleUrl: './active-quiz.component.css',
})
export class ActiveQuizComponent implements OnInit, OnDestroy {
  store: Store<AppState> = inject(Store<AppState>);
  private afs: Firestore = inject(Firestore);
  router: Router = inject(Router);
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  fb: FormBuilder = inject(FormBuilder);
  private logger: NGXLogger = inject(NGXLogger);
  private quizService = inject(QuizService);
  private aiService = inject(AiService);
  private utilsService = inject(UtilsService);

  groupId: string | null = this.activatedRoute.snapshot.paramMap.get('id')!;
  quizId: string | null = this.activatedRoute.snapshot.paramMap.get('quizId')!;
  attemptId: string | null = null;

  currentQuestion$: Observable<QuizQuestion | null | undefined> =
    new Observable();
  currentQuestion: QuizQuestion | null | undefined = null;

  answerForm: FormGroup = this.fb.group({
    answer: [null],
  });

  currQuestionIndex: number = 0;
  userScore: number = 0;
  remainingTime = signal<number>(0);
  formattedTimeMs = computed(() => this.remainingTime() * 1000);
  isValidating = signal<boolean>(false);

  private timerInterval: any;

  user: UserModel | null = null;
  group$: Observable<Group | undefined | null> = new Observable();
  quiz: Quiz | null = null;
  parentQuiz: Quiz | null = null;
  isReadOnly = signal<boolean>(false);
  answersList$: Observable<QuizQuestion[]> = of([]);
  myRoles$: Observable<any> = new Observable();
  roles: any = null;
  returnRoute = signal<string>('..');

  checkReadOnlyStatus() {
    const attemptUserId = this.quiz?.userId;
    const loggedInUserId = this.user?.uid;

    const isOwner = !!(attemptUserId && loggedInUserId && attemptUserId === loggedInUserId);
    
    let isParentQuizClosed = false;
    if (this.parentQuiz) {
      const isCompleted = this.parentQuiz.quizStatus === QuizStatus.completed;
      
      let isExpired = false;
      if (this.parentQuiz.endDate) {
        const now = new Date();
        const endDate: any = this.parentQuiz.endDate;
        const end = endDate.toDate ? endDate.toDate() : new Date(endDate);
        isExpired = end < now;
      }
      
      isParentQuizClosed = isCompleted || isExpired;
    }

    const readOnly = !isOwner || isParentQuizClosed || !!this.quiz?.completed;
    const prevReadOnly = this.isReadOnly();
    this.isReadOnly.set(readOnly);

    let hasAnalyticsAccess = false;
    if (this.roles && this.parentQuiz) {
      hasAnalyticsAccess = !!(
        this.roles.management ||
        (this.roles.faculty && this.parentQuiz.createdById === this.user?.uid)
      );
    }

    if (readOnly && hasAnalyticsAccess) {
      this.returnRoute.set('../analytics');
    } else {
      this.returnRoute.set('../details');
    }

    if (readOnly && !prevReadOnly) {
      this.loadAnswers();
    }
  }

  questionTypesMap = Object.keys(QuestionType).reduce(
    (acc, key) => {
      acc[key] = key;
      return acc;
    },
    {} as Record<string, string>,
  );

  isWorking$: Observable<boolean> = of(false);
  exit$ = new Subject<void>();

  constructor() {}

  ngOnDestroy(): void {
    this.cleanupStream();
  }

  ngOnInit() {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
        this.checkReadOnlyStatus();
      });

    this.group$ = this.store.select(groupSelector.getGroupById(this.groupId!));

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId!),
    );
    this.myRoles$.pipe(takeUntil(this.exit$)).subscribe((roles) => {
      this.roles = roles;
      this.checkReadOnlyStatus();
    });

    const parentQuizRef = doc(
      this.afs,
      `${environment.FIRESTORE_GROUPS_COLLECTION!}/${this.groupId}/${environment.FIRESTORE_QUIZZES_COLLECTION!}/${this.quizId}`,
    );
    docData(parentQuizRef)
      .pipe(takeUntil(this.exit$))
      .subscribe((quiz) => {
        if (quiz) {
          this.parentQuiz = quiz as Quiz;
          this.checkReadOnlyStatus();
        }
      });

    this.activatedRoute.queryParamMap.subscribe((params) => {
      this.attemptId = params.get('attemptId');
      if (this.attemptId) {
        this.startQuizListener();
      }
    });
  }

  syncTimer(startTime: number) {
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (!this.quiz || this.quiz.enableTimeLimit === false) {
      return;
    }

    // Create a reusable function to calculate the time
    const updateTimer = () => {
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      this.remainingTime.set(
        this.quiz?.remainingTime ?? this.quiz?.totalDuration! * 60,
      );

      if (this.remainingTime() <= 0) {
        if (this.timerInterval) clearInterval(this.timerInterval);
        this.logger.debug('Timer synced to 0 (Expired)');
      }
    };

    // If there is still time left, start the interval
    if (this.remainingTime() > 0) {
      this.timerInterval = setInterval(updateTimer, 1000);
    }
  }

  private startQuizListener(): void {
    const docRef = this.getAttemptDocRef();

    // Create a single stream for the document data
    const quizDoc$ = docData(docRef).pipe(takeUntil(this.exit$));

    // 1. Handle state synchronization (Timer, Index, Scores)
    quizDoc$.subscribe({
      next: (snapshot: DocumentData | undefined) => {
        this.isWorking$ = of(false);
        if (!snapshot) return;
        this.quiz = snapshot as Quiz;

        this.checkReadOnlyStatus();

        if (this.isReadOnly()) {
          this.logger.debug(
            'Quiz is read-only. Skipping active state sync.',
          );
          return;
        }

        this.currQuestionIndex = this.quiz.currentIndex ?? 0;
        this.userScore = this.quiz.userScore ?? 0;
        this.answerForm.reset();

        // Reset selection state for the new question
        this._selectedAnswerId = null;
        this._selectedAnswerText = '';
        this._isSubmitted = false;

        // Initialize or keep local timer stable
        if (
          this.quiz.remainingTime !== undefined &&
          this.remainingTime() === 0
        ) {
          this.remainingTime.set(this.quiz.remainingTime);
        }
        this.logger.debug(
          `Quiz state received: Remaining Time - ${this.remainingTime()}s, Current Index - ${this.currQuestionIndex}, User Score - ${this.userScore}`,
        );
      },
      error: (err) => this.logger.error('Firestore stream error:', err),
    });

    // 2. Safely map that same document stream straight to your UI question stream
    this.currentQuestion$ = quizDoc$.pipe(
      map((snapshot) => {
        const quizData = snapshot as Quiz;
        if (
          !quizData ||
          this.isReadOnly() ||
          quizData.completed ||
          !quizData.questions ||
          (quizData?.currentIndex ?? 0) >= quizData.questions.length
        ) {
          return null;
        }
        return quizData.questions[quizData.currentIndex ?? 0] || null;
      }),
      distinctUntilChanged(),
      switchMap((activeId) => {
        if (!activeId) {
          return of(null);
        }
        this.logger.debug(`Fetching question with ID: ${activeId}`);
        return this.quizService.getQuestionById(this.groupId!, activeId);
      }),
      shareReplay(1),
    );

    // Keep currentQuestion property in sync
    this.currentQuestion$.pipe(takeUntil(this.exit$)).subscribe((q) => {
      if (this.currentQuestion?.id === q?.id) return;
      this.currentQuestion = q;
      this.startLocalTimer(); // Resume local timer on question change
      this.isWorking$ = of(false); // Ensure loading state is reset on question change
      // this.logger.debug(
      //   `Current question updated: ${JSON.stringify(q, null, 2)}`,
      // );
    });
  }

  getQuestion(qId: string | null) {
    if (qId === null) {
      return of(null);
    }
    return this.quizService.getQuestionById(this.groupId!, qId);
  }

  private startLocalTimer() {
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (!this.quiz || this.quiz.enableTimeLimit === false) {
      return;
    }

    this.timerInterval = setInterval(() => {
      if (this.remainingTime() > 0) {
        this.remainingTime.update((time) => time - 1);
      } else {
        clearInterval(this.timerInterval);
        this.logger.debug('Timer Expired locally!');
        this.handleTimeExpired();
      }
    }, 1000);
  }

  // Add these helper variables if not already initialized
  _selectedAnswerId: string | null = null;
  _selectedAnswerText: string = '';
  _isSubmitted: boolean = false; // Toggled true when user confirms choice

  selectOption(opt: any) {
    this._selectedAnswerId = opt.id;
    this._selectedAnswerText = opt.text;
  }

  setTrueFalse(text: string) {
    this._selectedAnswerText = text.toLowerCase();
  }

  /**
   * Action when a user answers or skips a question
   */
  async submitAnswer() {
    if (!this.quiz || !this.currentQuestion || this._isSubmitted) return;

    this.isWorking$ = of(true);

    this._isSubmitted = true;
    const q = this.currentQuestion;
    let isCorrect = false;
    let aiExplanation = '';
    let aiTokenUsage: AiEvaluationResult['usage'] = undefined;

    this.logger.debug(
      `submitAnswer :: ${this.currQuestionIndex} : Submitted QuestionID: ${q.id}. Selected OptionId: ${this._selectedAnswerId}, or Text: "${this._selectedAnswerText}"`,
    );

    if (q.questionType === this.questionTypesMap['multipleChoice']) {
      isCorrect = this._selectedAnswerId === q.correctOptionId;
      this.logger.debug(
        `submitAnswer :: ${this.currQuestionIndex} : Multiple Choice Result: ${isCorrect}`,
      );
    } else if (q.questionType === this.questionTypesMap['trueFalse']) {
      this.logger.debug(
        `submitAnswer :: ${this.currQuestionIndex} : Evaluated True/False. User Answer: "${this._selectedAnswerText}", Expected: "${q.simpleAnswer}"`,
      );
      isCorrect =
        this._selectedAnswerText.trim().toLowerCase() ===
        q.simpleAnswer?.trim().toLowerCase();
      this.logger.debug(
        `submitAnswer :: ${this.currQuestionIndex} : True/False Result: ${isCorrect}`,
      );
    } else if (
      q.questionType === this.questionTypesMap['fillInTheBlank'] ||
      q.questionType === this.questionTypesMap['shortAnswer']
    ) {
      const threshold = this.quiz?.shortAnswerPercentage ?? 50;
      this.isValidating.set(true);
      try {
        const { result, explanation, usage } =
          await this.aiService.evaluateWithAI(this._selectedAnswerText, q, threshold);
        isCorrect = result;
        aiExplanation = explanation;
        aiTokenUsage = usage;
      } finally {
        this.isValidating.set(false);
      }
      this.logger.debug(
        `submitAnswer :: ${this.currQuestionIndex} : AI Evaluation Result - ${isCorrect ? '✅' : '❌'}. Explanation: ${aiExplanation}`,
      );
      if (aiTokenUsage) {
        this.logger.debug(
          `submitAnswer :: ${this.currQuestionIndex} : AI Token Usage - Prompt: ${aiTokenUsage.promptTokens}, Completion: ${aiTokenUsage.completionTokens}, Total: ${aiTokenUsage.totalTokens}`,
        );
      }
    }

    this.logger.debug(
      `submitAnswer :: ${this.currQuestionIndex} : Add Score: ${isCorrect ? (q.score ?? 1) : 0}. Preparing to update backend state...`,
    );

    const updatedScore = isCorrect
      ? this.userScore + (q.score ?? 1)
      : this.userScore;
    const nextIndex = this.currQuestionIndex + 1;
    const completed = !!(
      this.quiz.questions && nextIndex >= this.quiz.questions.length
    );

    await this.updateBackendState({
      nextIndex,
      newScore: updatedScore,
      timeLeft: completed ? 0 : this.remainingTime(),
      question: q,
      userAnswer: this._selectedAnswerText,
      isCorrect,
      aiExplanation,
      aiTokenUsage,
      completed,
    });

    if (!completed) {
      this.logger.debug(
        `submitAnswer :: ${this.currQuestionIndex} : Backend Updated. Score: ${updatedScore}. Moving to next question index: ${nextIndex}`,
      );
    }
  }

  async skipQuestion() {
    if (!this.quiz || !this.currentQuestion || this._isSubmitted) return;

    this._isSubmitted = true;
    const q = this.currentQuestion;
    const nextIndex = this.currQuestionIndex + 1;
    const completed = !!(
      this.quiz.questions && nextIndex >= this.quiz.questions.length
    );

    if (completed) {
      this.cleanupStream();
      this.isWorking$ = of(false);
    }

    await this.updateBackendState({
      nextIndex,
      newScore: this.userScore,
      timeLeft: completed ? 0 : this.remainingTime(),
      question: q,
      userAnswer: 'Skipped',
      isCorrect: false,
      completed,
    });
  }

  private async handleTimeExpired() {
    if (!this.quiz) return;
    this.cleanupStream();
    this.isWorking$ = of(false);

    const q = this.currentQuestion;
    const nextIndex = this.currQuestionIndex;

    await this.updateBackendState({
      nextIndex,
      newScore: this.userScore,
      timeLeft: 0,
      question: q,
      userAnswer: 'Time Expired',
      isCorrect: false,
      completed: true,
    });
  }

  private async updateBackendState({
    nextIndex,
    newScore,
    timeLeft,
    question,
    userAnswer,
    isCorrect,
    aiExplanation,
    aiTokenUsage,
    completed = false,
  }: {
    nextIndex: number;
    newScore: number;
    timeLeft: number;
    question?: QuizQuestion | null;
    userAnswer?: string;
    isCorrect?: boolean;
    aiExplanation?: string;
    aiTokenUsage?: AiEvaluationResult['usage'];
    completed?: boolean;
  }) {
    const docRef = this.getAttemptDocRef();
    try {
      const updateData: any = {
        currentIndex: nextIndex,
        userScore: newScore,
        remainingTime: completed ? 0 : timeLeft,
        completed: completed,
      };

      if (aiTokenUsage) {
        updateData.inputTokens = increment(aiTokenUsage.promptTokens);
        updateData.outputTokens = increment(aiTokenUsage.completionTokens);
        updateData.totalTokens = increment(aiTokenUsage.totalTokens);
      }

      await updateDoc(docRef, updateData);

      if (question && this.user && this.user.uid) {
        const responseDocRef = this.getUserResponseDocRef(question.id);
        const responseData: any = {
          ...question,
          attemptId: this.attemptId,
          userId: this.user.uid,
          userName: this.user.name || '',
          photoUrl: this.user.photoURL || '',
          userAnswer: userAnswer ?? '',
          aiExplanation: aiExplanation || '',
          isCorrect: isCorrect ?? false,
          modified: serverTimestamp(),
        };
        if (aiTokenUsage) {
          responseData.aiTokenUsage = aiTokenUsage;
        }
        await setDoc(responseDocRef, responseData);
      }
    } catch (error) {
      this.logger.error('Error updating backend state to Firestore:', error);
    }
  }

  exitQuiz() {
    this.cleanupStream();
    if (!this.quiz?.completed) {
      this.router.navigate(['../..'], {
        relativeTo: this.activatedRoute,
        replaceUrl: true,
      });
    }
  }

  async loadAnswers() {
    this.isWorking$ = of(true);
    if (
      !this.quiz ||
      !this.quiz.questions ||
      this.quiz.questions.length === 0
    ) {
      this.answersList$ = of([]);
      this.isWorking$ = of(false);
      return;
    }

    const targetUserId = this.quiz.userId || this.user?.uid!;
    this.answersList$ = of(
      await this.quizService.getUserAnswers(
        this.groupId!,
        this.quizId!,
        this.attemptId!,
        targetUserId,
      ),
    );
    this.isWorking$ = of(false);
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadHandler(event: Event): void {
    this.cleanupStream();
  }

  private cleanupStream(): void {
    if (this.exit$) {
      this.exit$.next();
      this.exit$.complete();
      this.logger.debug('Firestore quiz stream successfully closed.');
    }
  }

  private getAttemptDocRef() {
    return doc(
      this.afs,
      `${environment.FIRESTORE_GROUPS_COLLECTION!}/${this.groupId}/${environment.FIRESTORE_QUIZZES_COLLECTION!}/${this.quizId}/${environment.FIRESTORE_QUIZ_ATTEMPTS_COLLECTION!}/${this.attemptId}`,
    );
  }

  private getUserResponseDocRef(questionId: string) {
    return doc(
      this.afs,
      `${environment.FIRESTORE_GROUPS_COLLECTION!}/${this.groupId}/${environment.FIRESTORE_QUIZZES_COLLECTION!}/${this.quizId}/${environment.FIRESTORE_QUIZ_ATTEMPTS_COLLECTION!}/${this.attemptId}/${environment.FIRESTORE_USER_ANSWERS_COLLECTION!}/${questionId}`,
    );
  }

  getCorrectAnswer(item: QuizQuestion): string {
    if (item.correctOptionId) {
      const option = item.options?.find(
        (opt) => opt.id === item.correctOptionId,
      );
      return option ? option.text : 'N/A';
    }
    return item.simpleAnswer || 'N/A';
  }
}
